/**
 * The shared chart-tooltip store — spec §F2: "tooltips via shared `ChartTip` portal".
 *
 * The mockup has exactly one tooltip node that every chart moves and fills (`showTip(e,
 * html)` / `hideTip()`). This is that node's state, kept in a module-level store rather
 * than in React context because:
 *
 * * `showChartTip` has to be callable from an SVG or Leaflet handler that may not sit
 *   inside the React tree at all;
 * * charts stay pure markup — no per-point state, no re-render of a 300-point series on
 *   hover; only the tooltip component re-renders;
 * * one node means one z-index and one set of pixels to get right, and a tooltip that can
 *   never be clipped by a widget's `overflow:hidden` (the §F5.7 profile scrolls its body).
 *
 * Content is passed as **data, never as HTML** (the mockup used `innerHTML`): a label line
 * and an already-formatted value, so a unit or indicator name from the API cannot inject
 * markup into the page.
 */

export interface ChartTipState {
  /** Viewport coordinates of the pointer (the tip offsets itself from here). */
  x: number;
  y: number;
  /** Context line — e.g. `2024`, or `Burkina Faso · 2024`. */
  label: string;
  /** The formatted number, already carrying its unit. Rendered mono, in ink (§F2). */
  value: string;
}

let tip: ChartTipState | null = null;
const listeners = new Set<() => void>();

function emit(next: ChartTipState | null): void {
  tip = next;
  for (const listener of listeners) listener();
}

export function subscribeChartTip(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Current tooltip, or `null` when nothing is hovered. Also the test/reset read. */
export const readChartTip = (): ChartTipState | null => tip;

/** Show the shared tooltip at the pointer. `e` is any event carrying client coordinates. */
export function showChartTip(
  e: { clientX: number; clientY: number },
  label: string,
  value: string,
): void {
  emit({ x: e.clientX, y: e.clientY, label, value });
}

export function hideChartTip(): void {
  if (tip !== null) emit(null);
}
